/**
 * Tenant-scoped browser storage for the product tree.
 *
 * This is the single choke point every product module goes through to reach
 * localStorage (~450 call sites across ~90 files), which is why the isolation
 * rules live here rather than at each call site.
 *
 * WHAT CHANGED FROM THE PORTED VERSION, AND WHY
 *
 * 1. There is no default tenant. The original defaulted to `tenant-demo` and
 *    fell back to it whenever the active id was empty, so any read or write
 *    that happened before claims resolved — or while signed out — landed in a
 *    namespace shared by every user of that browser. Now an unscoped read
 *    returns its fallback and an unscoped write is dropped. Losing a write is
 *    recoverable; writing one tenant's data where another tenant reads it is
 *    not.
 *
 * 2. Keys are scoped by org AND branch, matching the server's
 *    `tenants/{org}/branches/{branch}` layout (see docs/TENANT_MODEL.md). A
 *    user moved between branches must not see the previous branch's cache.
 *
 * 3. Legacy bare-key migration MOVES rather than copies. The pre-tenant build
 *    wrote unprefixed keys like `siteline_jobs`. Copying meant the second
 *    tenant to sign in on that browser inherited the first tenant's records,
 *    because the bare key was still sitting there. The value is relocated into
 *    the first scope that claims it and the bare key is removed.
 *
 * None of this is a substitute for server-side enforcement. Firestore rules
 * and the Cloud Function tenant guard remain the actual boundary; this only
 * stops the client cache from being the weak link.
 */

import { MANAGED_IDENTITY_ENABLED } from '@/lib/managed-session';
import { requireManagedTransportSession } from '@/lib/managed-transport-session';

export type TenantScope = {
    orgId: string;
    branchId: string;
};

/**
 * Reads and writes are refused while this is null. The provider sets it during
 * render, before any product module can read, so there is no window in which
 * keys are built from an absent tenant.
 */
let scope: TenantScope | null = null;

/** Namespace for keys built while unscoped. Never read or written. */
const UNSCOPED = '__unscoped__';

export function setTenantScope(orgId: string, branchId: string): void {
    if (!orgId || !branchId) {
        scope = null;
        return;
    }
    scope = { orgId, branchId };
}

export function clearTenantScope(): void {
    scope = null;
}

export function getTenantScope(): TenantScope | null {
    return scope;
}

/** Org id from the current scope, or "" when unscoped. */
export function getActiveTenantId(): string {
    return scope?.orgId ?? '';
}

/** Storage key for the active org+branch. */
export function tenantKey(base: string): string {
    if (!scope) return `${UNSCOPED}:${base}`;
    if (MANAGED_IDENTITY_ENABLED) {
        try {
            const session = requireManagedTransportSession();
            if (session.tenantId !== scope.orgId || session.branchId !== scope.branchId) return `${UNSCOPED}:${base}`;
            return `managed:${JSON.stringify([session.uid, scope.orgId, scope.branchId])}:${base}`;
        } catch { return `${UNSCOPED}:${base}`; }
    }
    return `${scope.orgId}:${scope.branchId}:${base}`;
}

function canTouchStorage(): boolean {
    return typeof window !== 'undefined' && scope !== null && !tenantKey('').startsWith(UNSCOPED);
}

/**
 * Relocate a pre-tenant bare key into the current scope, once.
 *
 * Only the first scope to look for a given base claims it — the bare key is
 * removed on the way, so a second tenant signing in on the same browser starts
 * empty instead of inheriting the first tenant's records.
 */
export function migrateLegacyKey(base: string, ...legacyBases: string[]): string {
    const prefixed = tenantKey(base);
    if (!canTouchStorage()) return prefixed;
    // Unattributed legacy/offline caches remain intact; never assign them to a
    // newly authenticated principal. Central data hydrates the new owner namespace.
    if (MANAGED_IDENTITY_ENABLED) return prefixed;
    try {
        if (localStorage.getItem(prefixed) != null) return prefixed;

        // Ordered most- to least-specific. `${org}:${base}` is the pre-branch
        // scheme: it belongs to this org for certain, so it outranks the bare
        // pre-tenant key, which could have been written by anyone on this
        // browser. Without it, adding the branch segment would strand every
        // record already sitting in an existing user's cache.
        const candidates = [
            `${scope!.orgId}:${base}`,
            base,
            ...legacyBases,
        ];

        for (const candidate of candidates) {
            const raw = localStorage.getItem(candidate);
            if (raw != null) {
                localStorage.setItem(prefixed, raw);
                localStorage.removeItem(candidate);
                return prefixed;
            }
        }
    } catch {
        /* private mode / quota — fall through to the prefixed key */
    }
    return prefixed;
}

export function tenantGetItem(base: string, ...legacyBases: string[]): string | null {
    if (!canTouchStorage()) return null;
    try {
        return localStorage.getItem(migrateLegacyKey(base, ...legacyBases));
    } catch {
        return null;
    }
}

/**
 * Notified after a value changes, so the sync engine can persist it upstream.
 *
 * A callback rather than a direct import: this module is the one place every
 * product store depends on, and giving it a Firestore import would drag the
 * whole client SDK into every unit test and every module's import graph. The
 * engine registers itself at startup; with nothing registered this file behaves
 * exactly as it did before.
 */
type WriteListener = (base: string, raw: string | null) => void;

let writeListener: WriteListener | null = null;

export function setStorageWriteListener(listener: WriteListener | null): void {
    writeListener = listener;
}

function notify(base: string, raw: string | null): void {
    if (!writeListener) return;
    try {
        writeListener(base, raw);
    } catch {
        // A failing sync engine must not break the local write that succeeded.
    }
}

export function tenantSetItem(base: string, value: string): void {
    if (!canTouchStorage()) return;
    try {
        localStorage.setItem(tenantKey(base), value);
    } catch {
        // Quota exceeded. Still notify: the upstream copy is the durable one,
        // and dropping it here too would lose the write entirely.
    }
    notify(base, value);
}

export function tenantRemoveItem(base: string): void {
    if (!canTouchStorage()) return;
    try {
        localStorage.removeItem(tenantKey(base));
    } catch {
        /* ignore */
    }
    notify(base, null);
}

/**
 * Write a value that came FROM upstream into the local cache.
 *
 * Deliberately does not notify the write listener — echoing a hydrated value
 * back would re-upload every key on every login, and a slow round trip could
 * let a stale server value overwrite a newer local edit.
 */
export function hydrateTenantValue(base: string, raw: string | null): void {
    if (!canTouchStorage()) return;
    try {
        if (raw == null) localStorage.removeItem(tenantKey(base));
        else localStorage.setItem(tenantKey(base), raw);
    } catch {
        /* ignore */
    }
}

export function tenantGetJSON<T>(base: string, fallback: T, ...legacyBases: string[]): T {
    if (!canTouchStorage()) return fallback;
    try {
        const raw = tenantGetItem(base, ...legacyBases);
        if (raw == null) return fallback;
        const parsed = JSON.parse(raw) as unknown;
        // A list-shaped caller handed a non-list means the cached value
        // predates the current model; prefer the fallback over handing back
        // something the caller will iterate and crash on.
        if (Array.isArray(fallback) && !Array.isArray(parsed)) return fallback;
        return parsed as T;
    } catch {
        return fallback;
    }
}

export function tenantSetJSON(base: string, value: unknown): void {
    if (!canTouchStorage()) return;
    try {
        tenantSetItem(base, JSON.stringify(value));
    } catch {
        /* unserialisable value — dropping the write beats corrupting the slot */
    }
}
